/**
 * Idle Session Timeout Hook
 * VAPT Remediation: Issue 6 - Insufficient Session Expiration
 *
 * Monitors user activity (mouse, keyboard, touch, scroll) and triggers
 * automatic logout after a configurable period of inactivity.
 * Default timeout: 15 minutes (aligned with backend INACTIVE_SESSION_EXPIRY_IN_MINUTES)
 */

import { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootStateType } from '../redux-store/reducers';
import { logout } from '../redux-store/actions/auth';
import { LOGOUT_SUCCESS, SHOW_GLOBAL_ERROR } from '../redux-store/types/auth';

const IDLE_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes in milliseconds
const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
  'click',
];

export const useIdleTimeout = (timeoutMs: number = IDLE_TIMEOUT_MS): void => {
  const dispatch = useDispatch();
  const { token } = useSelector((state: RootStateType) => state.auth);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isLoggedOut = useRef(false);

  const handleLogout = useCallback(() => {
    if (isLoggedOut.current) return;
    isLoggedOut.current = true;

    // Clear the token from redux (triggers redirect to login)
    dispatch({ type: LOGOUT_SUCCESS });
    dispatch({
      type: SHOW_GLOBAL_ERROR,
      errorType: 'ALERT',
      message: 'Your session has expired due to inactivity. Please log in again.',
    });

    // Also call the backend logout endpoint to invalidate session server-side
    try {
      dispatch(logout() as any);
    } catch {
      // Silent fail - token is already cleared client-side
    }
  }, [dispatch]);

  const resetTimer = useCallback(() => {
    if (!token) return; // Don't start timer if not logged in

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      handleLogout();
    }, timeoutMs);
  }, [token, timeoutMs, handleLogout]);

  useEffect(() => {
    if (!token) {
      // Not authenticated, clear any existing timer
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      isLoggedOut.current = false;
      return;
    }

    // Reset logout flag on new login
    isLoggedOut.current = false;

    // Start the idle timer
    resetTimer();

    // Add activity event listeners
    const handleActivity = () => {
      resetTimer();
    };

    ACTIVITY_EVENTS.forEach((event) => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      // Cleanup
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      ACTIVITY_EVENTS.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, [token, resetTimer]);
};
